import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  CreditCard, 
  Search,
  Calendar,
  DollarSign,
  Building2,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Check,
  X
} from 'lucide-react';
import apiService from '../services/api';
import { 
  CompanyAffiliateHistoryResource, 
  CompanyCommissionWithdrawResource,
  AffiliateHistoriesResponse,
  CommissionWithdrawsResponse,
  AffiliateStats
} from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import RegisterReferrerModal from '../components/RegisterReferrerModal';
import ReferralFlagBadges from '../components/ReferralFlagBadges';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';

const Affiliate: React.FC = () => {
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [activeTab, setActiveTab] = useState<'histories' | 'withdraws'>('histories');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Affiliate Histories state
  const [affiliateHistories, setAffiliateHistories] = useState<CompanyAffiliateHistoryResource[]>([]);
  const [historiesPagination, setHistoriesPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  });
  
  // Commission Withdraws state
  const [commissionWithdraws, setCommissionWithdraws] = useState<CompanyCommissionWithdrawResource[]>([]);
  const [withdrawStatusFilter, setWithdrawStatusFilter] = useState<string>('');
  
  // Stats state
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  /**
   * Bukti transfer diminta di sini dan wajib: backend menolak tanpa itu, dan
   * inilah satu-satunya hal yang bisa ditunjukkan kalau penerima mengaku
   * uangnya tidak sampai.
   */
  const handleMarkSent = async (commissionId: string) => {
    const proof = window.prompt('Tautan bukti transfer (wajib):');
    if (!proof || proof.trim() === '') return;

    try {
      await apiService.markWithdrawSent(commissionId, proof.trim());
      loadCommissionWithdraws();
    } catch (err: any) {
      window.alert(err?.response?.data?.message ?? 'Gagal menandai pencairan terkirim.');
    }
  };

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'accept' | 'reject' | null;
    commissionId: string | null;
    commissionAmount: string;
    accountHolder: string;
  }>({
    isOpen: false,
    type: null,
    commissionId: null,
    commissionAmount: '',
    accountHolder: ''
  });

  // Load affiliate histories
  const loadAffiliateHistories = async (page: number = 1) => {
    setLoading(true);
    try {
      const response: AffiliateHistoriesResponse = await apiService.getAffiliateHistories(
        page, 
        historiesPagination.per_page, 
        searchTerm || undefined
      );
      setAffiliateHistories(response.data);
      setHistoriesPagination({
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
        per_page: response.meta.per_page,
        total: response.meta.total
      });
    } catch (error) {
      console.error('Error loading affiliate histories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load commission withdraws
  const loadCommissionWithdraws = async () => {
    setLoading(true);
    try {
      const response: CommissionWithdrawsResponse = await apiService.getCommissionWithdraws(
        withdrawStatusFilter || undefined
      );
      setCommissionWithdraws(response.data);
    } catch (error) {
      console.error('Error loading commission withdraws:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load affiliate stats
  const loadAffiliateStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiService.getAffiliateStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading affiliate stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle accept withdraw
  const handleAcceptWithdraw = async () => {
    if (!confirmDialog.commissionId) return;
    
    setActionLoading(confirmDialog.commissionId);
    try {
      await apiService.acceptWithdraw(confirmDialog.commissionId);
      // Reload data after successful action
      await loadCommissionWithdraws();
      await loadAffiliateStats(); // Reload stats as well
      setConfirmDialog({ isOpen: false, type: null, commissionId: null, commissionAmount: '', accountHolder: '' });
    } catch (error) {
      console.error('Error accepting withdraw:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject withdraw
  const handleRejectWithdraw = async () => {
    if (!confirmDialog.commissionId) return;
    
    setActionLoading(confirmDialog.commissionId);
    try {
      await apiService.rejectWithdraw(confirmDialog.commissionId);
      // Reload data after successful action
      await loadCommissionWithdraws();
      await loadAffiliateStats(); // Reload stats as well
      setConfirmDialog({ isOpen: false, type: null, commissionId: null, commissionAmount: '', accountHolder: '' });
    } catch (error) {
      console.error('Error rejecting withdraw:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Open confirm dialog
  const openConfirmDialog = (type: 'accept' | 'reject', withdraw: CompanyCommissionWithdrawResource) => {
    setConfirmDialog({
      isOpen: true,
      type,
      commissionId: withdraw.id,
      commissionAmount: formatCurrency(withdraw.netAmount),
      accountHolder: withdraw.companyBank.accountHolderName
    });
  };

  // Close confirm dialog
  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, type: null, commissionId: null, commissionAmount: '', accountHolder: '' });
  };

  // Load stats on component mount
  useEffect(() => {
    loadAffiliateStats();
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'histories') {
      loadAffiliateHistories();
    } else if (activeTab === 'withdraws') {
      loadCommissionWithdraws();
    }
  }, [activeTab]);

  // Load data when search term changes
  useEffect(() => {
    if (activeTab === 'histories') {
      loadAffiliateHistories(1);
    }
  }, [searchTerm]);

  // Load data when withdraw status filter changes
  useEffect(() => {
    if (activeTab === 'withdraws') {
      loadCommissionWithdraws();
    }
  }, [withdrawStatusFilter]);

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string, type: 'forwarded' | 'withdraw' = 'forwarded') => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (type === 'forwarded') {
      switch (status) {
        case 'true':
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              Forwarded
            </span>
          );
        case 'false':
          return (
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Pending
            </span>
          );
        default:
          return (
            <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
              {status}
            </span>
          );
      }
    } else {
      switch (status) {
        case 'requested':
          return (
            <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Requested
            </span>
          );
        case 'accepted':
          return (
            <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
              <Clock className="w-3 h-3 mr-1" />
              Disetujui
            </span>
          );
        case 'success':
          return (
            <span className={`${baseClasses} bg-green-100 text-green-800`}>
              <CheckCircle className="w-3 h-3 mr-1" />
              Terkirim
            </span>
          );
        case 'rejected':
          return (
            <span className={`${baseClasses} bg-red-100 text-red-800`}>
              <XCircle className="w-3 h-3 mr-1" />
              Rejected
            </span>
          );
        default:
          return (
            <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
              {status}
            </span>
          );
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Affiliate Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage affiliate histories and commission withdrawals
          </p>
        </div>
        <div className="mt-4 md:mt-0 md:ml-4">
          <button
            onClick={() => setRegisterOpen(true)}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Daftarkan Referrer
          </button>
        </div>
      </div>

      <RegisterReferrerModal
        isOpen={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegistered={(affiliate) => navigate(`/affiliate/company/${affiliate.companyId}`)}
      />

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg animate-pulse">
              <div className="p-5">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Commissions Pending */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Commissions Pending
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(stats.totalCommissionsPending)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Forwarded Commission */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Forwarded
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(stats.totalForwardedCommission)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Forwarded Commission Pending */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Forwarded Pending
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(stats.totalForwardedCommissionPending)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Forwarded Commission Withdrawn */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Withdrawn
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(stats.totalForwardedCommissionWithdrawn)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('histories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'histories'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Affiliate Histories
          </button>
          <button
            onClick={() => setActiveTab('withdraws')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'withdraws'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Commission Withdraws
          </button>
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        {activeTab === 'histories' && (
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by company name, referral code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
        )}
        
        {activeTab === 'withdraws' && (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setWithdrawStatusFilter(withdrawStatusFilter === 'requested' ? '' : 'requested')}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                withdrawStatusFilter === 'requested' 
                  ? 'bg-primary-50 border-primary-500 text-primary-700' 
                  : ''
              }`}
            >
              <Clock className="w-4 h-4 mr-2" />
              Pending Forwarded Only
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {activeTab === 'histories' ? (
            <>
              {affiliateHistories.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No affiliate histories</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No affiliate commission histories found.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {affiliateHistories.map((history) => (
                    <li key={history.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <Building2 className="h-8 w-8 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {history.subscribingCompany.companyName}
                                </p>
                                <span className="text-xs text-gray-500">
                                  ({history.subscribingCompany.brandName})
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-1">
                                <div className="flex items-center text-xs text-gray-500">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {history.subscriptionInvoice.invoiceNumber}
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(history.subscribeAt)}
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-mono">
                                    {history.companyAffiliate.referralCode}
                                  </span>
                                  {history.attribution?.source && (
                                    <span className="ml-2 text-[10px] text-gray-400">
                                      via {history.attribution.source === 'link' ? 'tautan'
                                        : history.attribution.source === 'manual_input' ? 'diketik'
                                        : 'koreksi admin'}
                                    </span>
                                  )}
                                </div>
                                <ReferralFlagBadges flags={history.fraudFlags} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="flex items-center text-sm font-medium text-gray-900">
                              {formatCurrency(history.totalCommission)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {history.companyAffiliate.commissionType === 'percent' 
                                ? `${history.companyAffiliate.commissionValue}%` 
                                : formatCurrency(history.companyAffiliate.commissionValue)
                              }
                            </div>
                          </div>
                          <div>
                            {getStatusBadge(history.isForwarded.toString(), 'forwarded')}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              
              {/* Pagination for histories */}
              {affiliateHistories.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200">
                  <Pagination
                    currentPage={historiesPagination.current_page}
                    total={historiesPagination.total}
                    perPage={historiesPagination.per_page}
                    onPageChange={loadAffiliateHistories}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {commissionWithdraws.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No commission withdraws</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No commission withdrawal requests found.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {commissionWithdraws.map((withdraw) => (
                    <li key={withdraw.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <CreditCard className="h-8 w-8 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => navigate(`/affiliate/company/${withdraw.companyId}`)}
                                  className="text-sm font-medium text-primary-600 hover:text-primary-900 truncate text-left"
                                >
                                  {withdraw.company.companyName}
                                </button>
                                <span className="text-xs text-gray-500">
                                  ({withdraw.company.brandName})
                                </span>
                              </div>
                              <div className="flex items-center space-x-4 mt-1">
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-mono">
                                    {withdraw.companyBank.accountNumber}
                                  </span>
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <span className="font-medium">
                                    {withdraw.companyBank.accountHolderName}
                                  </span>
                                </div>
                                <div className="flex items-center text-xs text-gray-500">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {formatDate(withdraw.createdAt)}
                                </div>
                                {withdraw.companyBank.isVerified && (
                                  <div className="flex items-center text-xs text-green-600">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Verified
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="flex items-center text-sm font-medium text-gray-900">
                              {formatCurrency(withdraw.netAmount)}
                            </div>
                            {withdraw.commissionTransferFee && (
                              <div className="text-xs text-gray-500">
                                Fee: {formatCurrency(withdraw.commissionTransferFee)}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <div>
                              {getStatusBadge(withdraw.withdrawStatus, 'withdraw')}
                            </div>
                            {canManage && withdraw.withdrawStatus === 'requested' && (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => openConfirmDialog('accept', withdraw)}
                                  disabled={actionLoading === withdraw.id}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === withdraw.id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  ) : (
                                    <Check className="w-3 h-3" />
                                  )}
                                </button>
                                <button
                                  onClick={() => openConfirmDialog('reject', withdraw)}
                                  disabled={actionLoading === withdraw.id}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === withdraw.id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                  ) : (
                                    <X className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            )}
                            {/* Persetujuan berhenti di `accepted`; uangnya baru
                                dianggap berangkat setelah buktinya masuk. */}
                            {canManage && withdraw.withdrawStatus === 'accepted' && (
                              <button
                                onClick={() => handleMarkSent(withdraw.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                              >
                                Tandai Terkirim
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeConfirmDialog}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                    confirmDialog.type === 'accept' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {confirmDialog.type === 'accept' ? (
                      <Check className="h-6 w-6 text-green-600" />
                    ) : (
                      <X className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {confirmDialog.type === 'accept' ? 'Accept Withdrawal' : 'Reject Withdrawal'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to {confirmDialog.type} this withdrawal request?
                      </p>
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Account Holder:</span> {confirmDialog.accountHolder}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Amount:</span> {confirmDialog.commissionAmount}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDialog.type === 'accept' ? handleAcceptWithdraw : handleRejectWithdraw}
                  disabled={actionLoading === confirmDialog.commissionId}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    confirmDialog.type === 'accept' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {actionLoading === confirmDialog.commissionId ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    confirmDialog.type === 'accept' ? 'Accept' : 'Reject'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeConfirmDialog}
                  disabled={actionLoading === confirmDialog.commissionId}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Affiliate;
